/**
 * Instanced volumetric shells (additive): light pillars, swirl/vortex cones and charged auras on
 * an open cylinder, protective bubbles on a sphere. Fresnel rims and scrolling noise keep them
 * readable at low opacity instead of solid bright shapes.
 */
import * as THREE from 'three';
import { groundY } from '../ground';
import { NOISE_GLSL } from '../style';

export const VOL = { pillar: 0, vortex: 1, aura: 2, bubble: 3, divine: 4 } as const;
export type VolKind = (typeof VOL)[keyof typeof VOL];

const VERT = `
attribute vec4 iPos; attribute vec4 iScale; attribute vec4 iColor; attribute vec4 iTime;
varying vec2 vUv; varying vec4 vColor; varying vec4 vTime; varying float vKind; varying float vRim;
void main(){
  vUv = uv; vColor = iColor; vTime = iTime; vKind = iScale.w;
  vec3 p = position;
  float taper = mix(1.0, iScale.z, clamp(uv.y, 0.0, 1.0));
  p.xz *= iScale.x * taper;
  p.y *= iScale.y;
  float r = iPos.w + iTime.w * iTime.y;
  float c = cos(r), s = sin(r);
  p.xz = vec2(p.x * c - p.z * s, p.x * s + p.z * c);
  vec3 w = iPos.xyz + p;
  if (iTime.x >= 1.0) w = vec3(0.0, -50.0, 0.0);
  vec4 mv = modelViewMatrix * vec4(w, 1.0);
  vec3 n = normalize(normalMatrix * vec3(normal.x * c - normal.z * s, normal.y, normal.x * s + normal.z * c));
  vRim = 1.0 - abs(dot(n, normalize(-mv.xyz)));
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = `
uniform float uFx;
varying vec2 vUv; varying vec4 vColor; varying vec4 vTime; varying float vKind; varying float vRim;
${NOISE_GLSL}
void main(){
  float t = vTime.x; if (t >= 1.0) discard;
  float age = vTime.y; float seed = vTime.z * 17.0;
  int kind = int(vKind + 0.5);
  float env = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.65, 1.0, t));
  vec3 col = vColor.rgb; float a;
  float y = vUv.y;
  if (kind == 0) {          // pillar of light
    float streak = noise(vec2(vUv.x * 14.0 + seed, y * 2.5 - age * 3.5));
    float vf = (1.0 - pow(y, 1.4)) * smoothstep(0.0, 0.06, y);
    a = (0.22 + 0.78 * pow(vRim, 1.5)) * (0.45 + 0.55 * streak) * vf;
    col = mix(col, vec3(1.0, 0.97, 0.9), pow(streak, 3.0) * 0.4);
  } else if (kind == 1) {   // swirl / vortex bands
    float s = fract(vUv.x * 3.0 + y * 1.4 - age * 1.8 + seed);
    float band = smoothstep(0.0, 0.05, s) * (1.0 - smoothstep(0.08, 0.32, s));
    float vf = smoothstep(0.0, 0.15, y) * (1.0 - smoothstep(0.7, 1.0, y));
    a = (band * 0.9 + pow(vRim, 3.0) * 0.12) * vf;
  } else if (kind == 2) {   // charged aura: crackling noise threads
    float n = noise(vec2(vUv.x * 10.0 + age * 2.0 + seed, y * 5.0 - age * 5.0));
    float thread = smoothstep(0.82, 0.97, 1.0 - abs(n * 2.0 - 1.0));
    float flick = 0.7 + 0.3 * step(0.5, fract(age * 11.0 + seed));
    float vf = smoothstep(0.0, 0.2, y) * (1.0 - smoothstep(0.6, 1.0, y));
    a = (thread * flick + pow(vRim, 4.0) * 0.1) * vf;
    col = mix(col, vec3(0.9, 0.95, 1.0), thread * 0.35);
  } else {                  // bubbles: fresnel shell with a moving shimmer band and faint hex grid
    float rim = pow(vRim, kind == 4 ? 2.0 : 2.6);
    float band = 1.0 - smoothstep(0.0, 0.06, abs(fract(y * 1.0 - age * 0.35 + seed) - 0.5));
    vec2 h = vec2(vUv.x * 24.0, y * 12.0);
    vec2 g = abs(fract(h + vec2(0.5 * floor(h.y), 0.0)) - 0.5);
    float hex = smoothstep(0.42, 0.5, max(g.x, g.y));
    a = rim * 0.85 + band * 0.12 + hex * 0.05 * (0.4 + rim);
    if (kind == 4) a += 0.06;
  }
  a *= env * vColor.a * uFx;
  if (a < 0.004) discard;
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
}`;

export class VolumeBatch {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  readonly pos: Float32Array;
  private readonly scale: Float32Array;
  private readonly color: Float32Array;
  private readonly time: Float32Array;
  private readonly aPos: THREE.InstancedBufferAttribute;
  private readonly aTime: THREE.InstancedBufferAttribute;
  private readonly aStatic: THREE.InstancedBufferAttribute[];
  private readonly age: Float32Array;
  private readonly life: Float32Array;
  private readonly hold: Float32Array;
  private readonly alive: Uint8Array;
  private readonly gen: Uint32Array;
  private hwm = 0;
  private staticDirty = false;

  constructor(readonly capacity: number, base: THREE.BufferGeometry, fx: { value: number }, renderOrder: number) {
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.getAttribute('position'));
    this.geo.setAttribute('normal', base.getAttribute('normal'));
    this.geo.setAttribute('uv', base.getAttribute('uv'));
    const mk = (): [Float32Array, THREE.InstancedBufferAttribute] => {
      const arr = new Float32Array(capacity * 4);
      return [arr, new THREE.InstancedBufferAttribute(arr, 4).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute];
    };
    let aScale: THREE.InstancedBufferAttribute;
    let aColor: THREE.InstancedBufferAttribute;
    [this.pos, this.aPos] = mk();
    [this.scale, aScale] = mk();
    [this.color, aColor] = mk();
    [this.time, this.aTime] = mk();
    this.aStatic = [aScale, aColor];
    this.geo.setAttribute('iPos', this.aPos);
    this.geo.setAttribute('iScale', aScale);
    this.geo.setAttribute('iColor', aColor);
    this.geo.setAttribute('iTime', this.aTime);
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.hold = new Float32Array(capacity).fill(-1);
    this.alive = new Uint8Array(capacity);
    this.gen = new Uint32Array(capacity);
    for (let i = 0; i < capacity; i++) this.time[i * 4] = 2;
    this.geo.instanceCount = 0;
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uFx: fx },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
  }

  /** Returns a handle (or -1). `holdAt` ≥ 0 keeps it alive at that phase until `release`. */
  spawn(kind: VolKind, x: number, y: number, z: number, radius: number, height: number, taper: number, r: number, g: number, b: number, a: number, lifeS: number, spin = 0, holdAt = -1): number {
    let i = -1;
    for (let k = 0; k < this.capacity; k++) {
      if (!this.alive[k]) {
        i = k;
        break;
      }
    }
    if (i < 0) return -1;
    this.alive[i] = 1;
    this.gen[i] = (this.gen[i]! + 1) & 0x7fff;
    if (i + 1 > this.hwm) this.hwm = i + 1;
    const o = i * 4;
    // y is above the floor; add the floor height (elevation) under (x, z).
    this.pos[o] = x;
    this.pos[o + 1] = y + groundY(x, -z);
    this.pos[o + 2] = z;
    this.pos[o + 3] = (i * 2.399) % 6.283;
    this.scale[o] = radius;
    this.scale[o + 1] = height;
    this.scale[o + 2] = taper;
    this.scale[o + 3] = kind;
    this.color[o] = r;
    this.color[o + 1] = g;
    this.color[o + 2] = b;
    this.color[o + 3] = a;
    this.time[o] = 0;
    this.time[o + 1] = 0;
    this.time[o + 2] = (i * 0.618) % 1;
    this.time[o + 3] = spin;
    this.age[i] = 0;
    this.life[i] = Math.max(0.05, lifeS);
    this.hold[i] = holdAt;
    this.staticDirty = true;
    return i | (this.gen[i]! << 16);
  }

  private slot(handle: number): number {
    if (handle < 0) return -1;
    const i = handle & 0xffff;
    return i < this.capacity && this.alive[i] && this.gen[i] === handle >>> 16 ? i : -1;
  }

  /** Move a live volume (follow a unit). */
  move(handle: number, x: number, y: number, z: number): void {
    const i = this.slot(handle);
    if (i < 0) return;
    this.pos[i * 4] = x;
    this.pos[i * 4 + 1] = y + groundY(x, -z);
    this.pos[i * 4 + 2] = z;
  }

  release(handle: number, fadeS = 0.35): void {
    const i = this.slot(handle);
    if (i < 0 || this.hold[i]! < 0) return;
    const t = Math.max(this.hold[i]!, 0.66);
    this.hold[i] = -1;
    this.life[i] = fadeS / Math.max(0.05, 1 - t);
    this.age[i] = t * this.life[i]!;
  }

  update(dt: number): void {
    let top = 0;
    for (let i = 0; i < this.hwm; i++) {
      if (!this.alive[i]) continue;
      const o = i * 4;
      this.time[o + 1] = this.time[o + 1]! + dt;
      if (this.hold[i]! >= 0) {
        this.age[i] = Math.min(this.age[i]! + dt, this.hold[i]! * this.life[i]!);
        this.time[o] = this.age[i]! / this.life[i]!;
        top = i + 1;
        continue;
      }
      this.age[i] = this.age[i]! + dt;
      const t = this.age[i]! / this.life[i]!;
      if (t >= 1) {
        this.alive[i] = 0;
        this.time[o] = 2;
        continue;
      }
      this.time[o] = t;
      top = i + 1;
    }
    this.hwm = top;
    this.geo.instanceCount = this.hwm;
    if (this.hwm > 0) {
      this.aTime.needsUpdate = true;
      this.aPos.needsUpdate = true;
    }
    if (this.staticDirty) {
      this.staticDirty = false;
      for (const a of this.aStatic) a.needsUpdate = true;
    }
  }

  clear(): void {
    this.alive.fill(0);
    this.hold.fill(-1);
    for (let i = 0; i < this.capacity; i++) this.time[i * 4] = 2;
    this.hwm = 0;
    this.geo.instanceCount = 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
